import { Empty, Segmented } from 'antd';
import { CreateChatModal } from 'components/Modals';
import RowLoader from 'components/Sceletons/RowLoader';
import { IDocument, membersType, IChat } from 'interfaces';
import Mark from 'mark.js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { createGLobalChat, fetchChatHistory, fetchGlobalChats, fetchSetSelectedChat } from 'store/reducers/chatCreator';
import { setIsOpenCreateModal, setListStatus, setMessageForReply } from 'store/slices/chatsSlice';
import classes from './ChatTab.module.scss';
import ChatTabControls from './ChatTabControls';
import ChatTabItem from './ChatTabItem';
import { setSelectedDocument, setViewType } from 'store/slices/dataDocumentsSlice';
import { setDocsSegment, setIsUploadingSomeChat } from 'store/slices/windowStateSlice';
import api from 'api';
import { checkIsTariffEnded, checkIsAwaitingPayment } from 'helpers/tariffPayment';

function ChatTab() {
  const [currentSegment, setCurrentSegment] = React.useState<'main' | 'room'>('main');
  const [searchValue, setSearchValue] = React.useState<string[]>([]);
  const [documents, setDocuments] = React.useState<IDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = React.useState(false);
  
  const { messageApi } = useAppSelector((state) => state.windowState);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { userData, available_rooms } = useAppSelector((state) => state.userData);
  const { globalUsers } = useAppSelector((state) => state.dataUsers);
  const {
    roomAllChats,
    chatData,
    selectedChat,
    isLoadedSession,
    roomChatLoading,
    globalChats,
    isOpenCreateModal,
    globalChatLoading,
    listStatus,
    allTagsDocuments,
  } = useAppSelector((state) => state.chat);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { room, chatId } = useParams();
  const { pathname } = useLocation();
  const lastRequestedRoomIdRef = React.useRef<string | null>(null);  

  const privatedRooms = ['143d9fd4-96b7-46e8-819f-9515a04eaff9'];
  const isAdmin = available_rooms.filter((room) => privatedRooms.includes(room.id))[0]?.permissions?.can_edit_roles;
  
  const isLoadingChat = !isLoadedSession || (dataRoom?.id && roomChatLoading) || (currentSegment === 'main' && globalChatLoading);
  
  const isDisabledSegments = Boolean(
    isLoadingChat ||
      (dataRoom?.id && privatedRooms.includes(dataRoom?.id) && !isAdmin)
  );

  const isUnpaidRoom = checkIsAwaitingPayment(dataRoom);
  const isEndedTariff = checkIsTariffEnded(dataRoom);
  const isNeedToAccessTerms = dataRoom?.access_terms_status == 'waiting_for_approve';

  const getAllRoomDocuments = async () => {
    if (
      currentSegment === 'room' &&
      dataRoom?.id &&
      lastRequestedRoomIdRef.current !== dataRoom?.id
    ) {
      setIsLoadingDocuments(true);
      setDocuments([]);
      try {
        const response = await api.getAllDocumentsOfRoom(dataRoom?.id);
        setDocuments(response.data);
        setIsLoadingDocuments(false);
      } catch (error) {
        messageApi?.error(t('Documents.error.receivingData'));
        setIsLoadingDocuments(false);
      }
      lastRequestedRoomIdRef.current = dataRoom?.id;
    }
  };

  const onCreateModalOpen = async() => {
    dispatch(setIsOpenCreateModal(true));
    await getAllRoomDocuments();
   
  };
  const getChatRoute = (chat: IChat) => {
    const roomId = dataRoom?.custom_url || dataRoom?.id;
    const paths = [
      `/room/${roomId}/chat/document/`,
      `/room/${roomId}/chat/`,
      `/room/${roomId}/chat/user/`,
      `/global-chats/`,
      `/room/${roomId}/chat/telegram/`,
      `/global-chats/support/`,
    ];
    return paths[chat.entity_type] + (chat.entity_type === 1 ? '' : chat.id);
  };
  const actionCases = {
    main: {
      action: createGLobalChat,
    },
    room: {
      action: createGLobalChat,
    },
  };

  React.useEffect(() => {
    const pathList = pathname.split('/');
    const segment = !pathList.includes('global-chats') && room ? 'room' : 'main';
    setCurrentSegment(segment)
    dispatch(setListStatus(segment));
  }, [room]);

  React.useEffect(() => {
    if (selectedChat?.id) { 
      const isMainSegment = selectedChat.entity_type === 3 || selectedChat.entity_type === 5;
      setCurrentSegment(isMainSegment ? 'main' : 'room');
    }
  }, [selectedChat]);
  
  React.useEffect(() => {
    const currentChatId = chatId || localStorage.getItem('previousChatId');
    const isNeedToUploadGlobalChats = !globalChats[0] && currentSegment === 'main';
    isNeedToUploadGlobalChats && dispatch(fetchGlobalChats({ ownerId: userData?.id!, chatId: currentChatId || null }));
  }, [currentSegment]);

  const markText = (text: string, markerRef: React.RefObject<HTMLDivElement>, timeout?: number) => {
    const markInstance = new Mark(markerRef.current as HTMLElement);

    setTimeout(() => {
      markInstance.unmark({
        className: classes.mark,
        done: () => {
          markInstance.mark(text, {
            className: classes.mark,
          });
        },
      });
    }, timeout || 100);
  };

  const onCancelCreateModal = () => {
    dispatch(setListStatus(currentSegment));
    dispatch(setIsOpenCreateModal(false));
  };

  const onClickItem = async (member: membersType) => {
    try {
      const data = {
        members_ids: [userData?.id!, member.id],
        entity_type: 3,
        room_id: dataRoom?.id!,
        title: member.email,
      };

      onCancelCreateModal();
      const { payload } = (await dispatch(actionCases[currentSegment].action({ data, ownerId: userData?.id! }))) as any;
      navigate(getChatRoute(payload.chat));
    } catch (err) {
      messageApi?.error(t('Chats.createChatError'));
    }
  };

  const onChangeSegment = (value: any) => {
    setCurrentSegment(value);
    dispatch(setListStatus(value));
  };

  const { localUesrlist, globalUserList } = React.useMemo(() => {
    const localUesrlist = dataRoom?.available_users?.map(({ last_name, first_name, id, email }) => ({
      name: `${first_name} ${last_name}`,
      email,
      id,
    }));

    const globalUserList = globalUsers.map(({ last_name, first_name, id, email }) => ({
      name: `${first_name} ${last_name}`,
      email,
      id,
    }));

    return { globalUserList, localUesrlist };
  }, [dataRoom, globalUsers]);

  const segmentOptions = [
    {
      label: t('Chats.room'),
      value: 'room',
      disabled: isUnpaidRoom || isEndedTariff || isNeedToAccessTerms,
    },
    {
      label: t('Chats.main'),
      value: 'main',
    },
  ];

  const chatCases = {
    main: globalChats,
    room: roomAllChats,
  };

  const listCases = {
    main: globalUserList,
    room: localUesrlist,
    groupList: selectedChat?.members.map((member) => member),
  };
  
  const filteredChats = React.useMemo(() => {
    const list = !searchValue.length
      ? chatCases[currentSegment as keyof typeof chatCases]
      : [...globalChats, ...roomAllChats].filter((chat) =>
          searchValue.some((term) =>
            `${t(`Chats.types.${chat.entity_type}`)} ${chat.entity_name || chat.title} ${
              chat.entity_type === 2 && chat.title
            }`
              .toLowerCase()
              .includes(term.toLowerCase())
          )
        );
    return [...list].sort((a, b) => (b.entity_type === 5 ? 1 : 0) - (a.entity_type === 5 ? 1 : 0));
  }, [searchValue, globalChats, roomAllChats, currentSegment]);

  return (
    <div className={classes.chatTab}>
      <ChatTabControls
        t={t}
        searchValue={searchValue}
        isLaoding={isLoadingChat}
        setSearchValue={setSearchValue}
        onCreateModalOpen={onCreateModalOpen}
      />
      <div className={classes.segmentWrap}>
        <Segmented
          options={segmentOptions}
          value={currentSegment}
          disabled={isDisabledSegments}
          onChange={onChangeSegment}
          block
        />
      </div>

      {isLoadingChat ? (
        <div style={{ margin: '0 8px 0 0' }}>
          <RowLoader width={600} padding={'0 0 0 6px'} height={60} borderradius={6} marginTop={1} />
          <RowLoader width={600} padding={'0 0 0 6px'} height={60} borderradius={6} marginTop={1} />
        </div>
      ) : (
        <div className={classes.chatTab_tabs}>
          {!filteredChats.length ? (
            <Empty style={{fontSize: 16}} description={t('Chats.emptyText')} />
          ) : (
            filteredChats.map((chat) => {
              const onTabClick = async () => {
                navigate(getChatRoute(chat));
                if (!chatData[chat.id] || !chatData[chat.id].isLoadedHistory) {
                  dispatch(fetchSetSelectedChat({...chat, id: '0'}));
                  dispatch(setIsUploadingSomeChat(true));
                  await dispatch(fetchChatHistory({ chat_id: chat.id, room_id: chat?.room_id!, page: 1 }));
                }
                
                if (chat?.entity_type === 0) {
                  dispatch(setIsUploadingSomeChat(true));
                  if (!chat?.deleted_at) {
                    const targetDocument = allTagsDocuments.find((doc) => doc.id === chat!.entity_id);
                    const document = targetDocument ? {data: targetDocument} : await api.getDocumentById(String(chat!.entity_id));                    
                    dispatch(setSelectedDocument(document.data));
                  } else {
                    dispatch(setSelectedDocument({name: chat.title, deleted_at: chat?.deleted_at} as IDocument));
                  }
                  dispatch(setViewType('Document'));
                  dispatch(setDocsSegment('chat'));
                } else {
                  dispatch(setSelectedDocument(null));
                  dispatch(setViewType('Table'));
                  dispatch(setDocsSegment('view'));
                }
                
                dispatch(fetchSetSelectedChat(chat));
                
                dispatch(setMessageForReply(null));
                dispatch(setIsUploadingSomeChat(false));
              };

              return (
                <ChatTabItem
                  key={chat.id}
                  onTabClick={onTabClick}
                  markText={markText}
                  searchValue={searchValue}
                  selectedChat={selectedChat!}
                  chat={chat}
                />
              );
            })
          )}
        </div>
      )}

      {isOpenCreateModal && (
        <React.Suspense fallback={<div />}>
          <CreateChatModal
            t={t}
            members={listCases[listStatus]!}
            ownerId={userData?.id!}
            onClickItem={onClickItem}
            open={isOpenCreateModal}
            onCancel={onCancelCreateModal}
            title={t('Chats.modals.create.title')}
            currentSegment={currentSegment}
            documents={documents}
            isLoadingDocuments={isLoadingDocuments}
          />
        </React.Suspense>
      )}
    </div>
  );
}

export default ChatTab;
